import React, { useState } from 'react';
import useSongMetadataSearch from '../../pages/EmployeePortal/LessonDashboard/hooks/useSongMetadataSearch';
import { buildMeta } from '../../pages/EmployeePortal/LessonDashboard/sheets/AddSongSheet';
import { ensureCanonicalSong } from '../../services/SongRegistryService';
import { linkRepertoireEntryToSong } from '../../services/RepertoireService';
import './LinkSongControl.css';

/**
 * Resolve one UNLINKED repertoire row to a `songs` registry doc.
 *
 * WHY THIS EXISTS. `canonicalSongId` is the studio-owned song identity
 * (repertoire arc question A, 2026-08-05): "every student singing this song" is
 * the set of repertoire rows sharing one, and that is the join the Recital
 * planning page needs. A row minted before the registry existed — or from a
 * teacher's shorthand with no metadata to resolve — carries an explicit null and
 * wears the "Not linked" badge. This is the tool that clears it.
 *
 * TWO PATHS, CHEAPEST FIRST.
 *  1. One-tap. A row that already carries metadata needs no typing and no
 *     search: `ensureCanonicalSong` resolves the stored blob against the
 *     registry (external refs, then title+artist) and mints only if nothing
 *     matches. The 2026-08-05 backfill found every prod row in this state, so
 *     this is expected to be the common case, not the fallback.
 *  2. Search. For rows with no metadata at all — `Crazy for you`, created by the
 *     orphan repair — the Add-a-song search is the only way to say WHICH song
 *     this is.
 *
 * REUSE, DELIBERATELY ACROSS A LAYER BOUNDARY. `useSongMetadataSearch` lives
 * under LessonDashboard but its docblock states it "only SEARCHES … so this
 * stays reusable from any sheet". This is the first non-lesson caller, which is
 * exactly what that design anticipated; duplicating the three-API fan-out here
 * would be the worse trade. `buildMeta` comes along for the same reason — the
 * result rows must read identically to the ones the teacher already knows from
 * Add-a-song.
 *
 * NOTE WHAT IS *NOT* PASSED: `studentId`. The hook short-circuits on an existing
 * repertoire entry's metadata, and here that would hand back the very blob that
 * failed to identify the song (or nothing at all, for a metadata-less row) —
 * the one answer that cannot help. Path 1 above already covers the stored
 * metadata; a search is by definition the case where it did not suffice.
 */
const LinkSongControl = ({ entry, onLinked, onCancel }) => {
	const savedTitle = entry?.metadata?.title?.trim() || '';
	const [query, setQuery] = useState(savedTitle || entry?.songName || '');
	const [busy, setBusy] = useState(false);
	const [searched, setSearched] = useState(false);
	const [error, setError] = useState('');
	const { search, resolveOption, options, isSearching, searchError, reset } =
		useSongMetadataSearch({});

	/**
	 * The whole write, both paths funnel through here.
	 *
	 * `ensureCanonicalSong` NEVER throws — a registry failure returns null so a
	 * lesson is never blocked — so null is the "couldn't identify it" branch and
	 * is reported rather than swallowed. Everything the service throws (already
	 * linked, row deleted) surfaces its own message: an already-linked row means
	 * this screen is stale, and telling the teacher that beats a silent no-op.
	 */
	const linkWith = async (metadata) => {
		if (busy || !metadata) return;
		setBusy(true);
		setError('');
		try {
			const canonicalSongId = await ensureCanonicalSong(metadata);
			if (!canonicalSongId) {
				setError("Couldn't identify that song. Try searching for it instead.");
				setBusy(false);
				return;
			}
			await linkRepertoireEntryToSong(entry.id, canonicalSongId, metadata);
			// Parent unmounts this control on success, so `busy` is deliberately
			// left true — re-enabling the buttons for one frame invites a second
			// click on a row that is already linked.
			onLinked(canonicalSongId, metadata);
		} catch (err) {
			console.error('Error linking repertoire row to canonical song:', err);
			setError(err.message || 'That link did not save. Try again.');
			setBusy(false);
		}
	};

	const runSearch = async () => {
		const trimmed = query.trim();
		if (!trimmed || isSearching || busy) return;
		setSearched(true);
		setError('');
		const result = await search(trimmed);
		// An unambiguous single hit links directly — the teacher has nothing to
		// choose between. 'options' renders the rows below; 'none' falls through
		// to the no-matches line.
		if (result.kind === 'resolved') await linkWith(result.metadata);
	};

	const onQueryChange = (e) => {
		setQuery(e.target.value);
		if (searched) {
			setSearched(false);
			reset();
		}
	};

	return (
		<div className="link-song">
			<div className="link-song__label">
				<i className="fa-solid fa-link" aria-hidden="true"></i>
				Link “{entry?.songName}” to a canonical song
			</div>
			<p className="link-song__why">
				Linking lets this song join across students — it&apos;s how a recital set list
				finds everyone singing it.
			</p>

			{savedTitle && (
				<button
					type="button"
					className="link-song__onetap"
					disabled={busy}
					onClick={() => linkWith(entry.metadata)}
				>
					<i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
					<span className="link-song__onetap-text">
						<span className="link-song__onetap-action">Link using saved info</span>
						<span className="link-song__onetap-detail">
							{[savedTitle, entry.metadata.artist].filter(Boolean).join(' — ')}
						</span>
					</span>
				</button>
			)}

			<div className="link-song__searchfield">
				<i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
				<input
					className="link-song__input"
					type="text"
					value={query}
					placeholder="Song title"
					enterKeyHint="search"
					aria-label="Search for the song to link"
					onChange={onQueryChange}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							runSearch();
						}
					}}
				/>
				<button
					type="button"
					className="link-song__search"
					disabled={!query.trim() || isSearching || busy}
					onClick={runSearch}
				>
					Search
				</button>
			</div>

			{isSearching && <p className="link-song__status">Searching…</p>}
			{searchError && <p className="link-song__status link-song__status--error">{searchError}</p>}
			{error && <p className="link-song__status link-song__status--error">{error}</p>}
			{searched && !isSearching && !searchError && options.length === 0 && (
				<p className="link-song__status">No matches. Try adding the show or artist.</p>
			)}

			{options.length > 0 && (
				<ul className="link-song__results">
					{options.map((metadata, i) => (
						<li key={metadata.recordingId || metadata.musicbrainzId || `row-${i}`}>
							<div className="link-song__result">
								<span className="link-song__resulttext">
									<span className="link-song__resulttitle">{metadata.title}</span>
									<span className="link-song__resultmeta">
										{buildMeta(metadata, metadata.title)}
									</span>
								</span>
								<button
									type="button"
									className="link-song__pick"
									disabled={busy}
									onClick={async () => {
										const resolved = await resolveOption(metadata);
										await linkWith(resolved.metadata);
									}}
								>
									Link
								</button>
							</div>
						</li>
					))}
				</ul>
			)}

			<button type="button" className="link-song__cancel" disabled={busy} onClick={onCancel}>
				Cancel
			</button>
		</div>
	);
};

export default LinkSongControl;
